export interface Section {
  id: string;
  title: string;
  content: string;
}

export const sections: Section[] = [
  {
    id: 'overview',
    title: 'Overview',
    content: `
# TTLock API Adapter for WeLock

## Overview

This Flask-based API adapter provides a TTLock-compatible interface for interacting with WeLock smart locks. The adapter translates API requests from TTLock format to WeLock format, allowing applications designed for TTLock to work with WeLock devices.

## Base URL

\`\`\`
Will be provided upon request
\`\`\`
    `
  },
  {
    id: 'implementation-details',
    title: 'Implementation Details',
    content: `
## Implementation Details

The adapter handles:
- Token management and authentication
- Mapping between TTLock and WeLock API formats
- Device and gateway mapping
- Asynchronous command execution
- Error code translation
    `
  },
  {
    id: 'authentication',
    title: 'Authentication',
    content: `
## Authentication

### Get Access Token

**Request URL**
\`\`\`
/v3/auth/token
\`\`\`

**HTTP Method**
\`\`\`
POST
\`\`\`

**Request Parameters**

| Parameter | Type | Required | Description |
| --------- | ---- | -------- | ----------- |
| clientId | String | Yes | Your client ID |
| clientSecret | String | Yes | Your client secret |

**Response**

\`\`\`json
{
  "data": {
    "accessToken": "TOKEN_STRING",
    "refreshToken": "REFRESH_TOKEN_STRING",
    "expiresIn": 7200
  },
  "errorCode": 0,
  "errorMsg": "success"
}
\`\`\`
    `
  },
  {
    id: 'user-management',
    title: 'User Management',
    content: `
## User Management

### Register User

**Request URL**
\`\`\`
/user/register
\`\`\`

**HTTP Method**
\`\`\`
POST
\`\`\`

**Request Parameters**

| Parameter | Type | Required | Description |
| --------- | ---- | -------- | ----------- |
| account | String | Yes | User account (email or phone) |
| password | String | Yes | User password |
| verifyCode | String | Yes | Verification code |

**Response**

\`\`\`json
{
  "errorCode": 0,
  "errorMsg": "success"
}
\`\`\`

### User Login

**Request URL**
\`\`\`
/user/login
\`\`\`

**HTTP Method**
\`\`\`
POST
\`\`\`

**Request Parameters**

| Parameter | Type | Required | Description |
| --------- | ---- | -------- | ----------- |
| account | String | Yes | User account (email or phone) |
| password | String | Yes | User password |

**Response**

\`\`\`json
{
  "data": {
    "userId": 12345,
    "nickname": "username",
    "account": "user@example.com",
    "accessToken": "USER_TOKEN_STRING",
    "email": "user@example.com",
    "hotelFlag": 1
  },
  "errorCode": 0,
  "errorMsg": "success"
}
\`\`\`

### User Logout

**Request URL**
\`\`\`
/user/logout
\`\`\`

**HTTP Method**
\`\`\`
POST
\`\`\`

**Request Headers**

| Parameter | Type | Required | Description |
| --------- | ---- | -------- | ----------- |
| accessToken | String | Yes | Access token |
| userId | String | Yes | User ID |

**Response**

\`\`\`json
{
  "errorCode": 0,
  "errorMsg": "success"
}
\`\`\`

### Send Verification Code

**Request URL**
\`\`\`
/user/sendVerifyCode
\`\`\`

**HTTP Method**
\`\`\`
POST
\`\`\`

**Request Parameters**

| Parameter | Type | Required | Description |
| --------- | ---- | -------- | ----------- |
| account | String | Yes | User account (email or phone) |
| type | Int | Yes | Verification type (1: register, 2: reset password) |

**Response**

\`\`\`json
{
  "errorCode": 0,
  "errorMsg": "success"
}
\`\`\`
    `
  },
  {
    id: 'lock-management',
    title: 'Lock Management',
    content: `
## Lock Management

### Lock List

**Request URL**
\`\`\`
/v3/lock/list
\`\`\`

**HTTP Method**
\`\`\`
GET
\`\`\`

**Request Parameters**

| Parameter | Type | Required | Description |
| --------- | ---- | -------- | ----------- |
| accessToken | String | Yes | Access token |
| pageNo | Int | No | Page number, starting from 1, default: 1 |
| pageSize | Int | No | Items per page, default: 20 |

**Response**

\`\`\`json
{
  "list": [
    {
      "lockId": "22130777",
      "lockName": "22130777",
      "lockMac": "",
      "lockAlias": "22130777",
      "electricQuantity": 50,
      "lockStatus": 1,
      "hasPassword": true,
      "hasCard": true,
      "hasFingerprint": false,
      "featureValue": "PCB10EBL41",
      "timezoneRawOffset": 0,
      "modelNum": "PCB10EBL41"
    }
  ],
  "totalPages": 1,
  "errorCode": 0,
  "errorMsg": "success"
}
\`\`\`

### Get Battery Level

**Request URL**
\`\`\`
/v3/lock/getBatteryLevel
\`\`\`

**HTTP Method**
\`\`\`
GET
\`\`\`

**Request Parameters**

| Parameter | Type | Required | Description |
| --------- | ---- | -------- | ----------- |
| accessToken | String | Yes | Access token |
| lockId | String | Yes | Lock ID |

**Response**

\`\`\`json
{
  "electricQuantity": 75,
  "errorCode": 0,
  "errorMsg": "success"
}
\`\`\`

### Unlock

**Request URL**
\`\`\`
/v3/lock/unlock
\`\`\`

**HTTP Method**
\`\`\`
GET
\`\`\`

**Request Parameters**

| Parameter | Type | Required | Description |
| --------- | ---- | -------- | ----------- |
| accessToken | String | Yes | Access token |
| lockId | String | Yes | Lock ID |

**Response**

\`\`\`json
{
  "lockTime": 1712325684000,
  "errorCode": 0,
  "errorMsg": "success"
}
\`\`\`

### Query Lock State

**Request URL**
\`\`\`
/lock/queryState
\`\`\`

**HTTP Method**
\`\`\`
GET
\`\`\`

**Request Parameters**

| Parameter | Type | Required | Description |
| --------- | ---- | -------- | ----------- |
| accessToken | String | Yes | Access token |
| lockId | String | Yes | Lock ID |

**Response**

\`\`\`json
{
  "data": {
    "state": 0
  },
  "errorCode": 0,
  "errorMsg": "success"
}
\`\`\`

*Note: state values: 0-locked, 1-unlocked, 2-unknown*

### Set Auto Lock Time

**Request URL**
\`\`\`
/lock/setAutoLockTime
\`\`\`

**HTTP Method**
\`\`\`
POST
\`\`\`

**Request Parameters**

| Parameter | Type | Required | Description |
| --------- | ---- | -------- | ----------- |
| accessToken | String | Yes | Access token |
| lockId | String | Yes | Lock ID |
| autolockSec | Int | Yes | Auto lock time in seconds |
| byGateway | Int | No | Whether to use gateway (1: yes, 0: no), default: 0 |

**Response**

\`\`\`json
{
  "errorCode": 0,
  "errorMsg": "success"
}
\`\`\`
    `
  },
  {
    id: 'passcode-management',
    title: 'Passcode Management',
    content: `
## Passcode Management

### Create Passcode

**Request URL**
\`\`\`
/passcode/create
\`\`\`

**HTTP Method**
\`\`\`
POST
\`\`\`

**Request Parameters**

| Parameter | Type | Required | Description |
| --------- | ---- | -------- | ----------- |
| accessToken | String | Yes | Access token |
| lockId | String | Yes | Lock ID |
| passcodeName | String | Yes | Passcode name |
| type | Int | Yes | Passcode type (1: permanent, 2: temporary) |
| startDate | Long | No | Start date in milliseconds |
| endDate | Long | No | End date in milliseconds |

**Response**

\`\`\`json
{
  "data": {
    "passcodeId": 12345,
    "passcode": "123456"
  },
  "errorCode": 0,
  "errorMsg": "success"
}
\`\`\`

### Delete Passcode

**Request URL**
\`\`\`
/passcode/delete
\`\`\`

**HTTP Method**
\`\`\`
POST
\`\`\`

**Request Parameters**

| Parameter | Type | Required | Description |
| --------- | ---- | -------- | ----------- |
| accessToken | String | Yes | Access token |
| passcodeId | String | Yes | Passcode ID |
| byGateway | Int | No | Whether to use gateway (1: yes, 0: no), default: 0 |

**Response**

\`\`\`json
{
  "errorCode": 0,
  "errorMsg": "success"
}
\`\`\`
    `
  },
  {
    id: 'gateway-management',
    title: 'Gateway Management',
    content: `
## Gateway Management

### Gateway List

**Request URL**
\`\`\`
/gateway/list
\`\`\`

**HTTP Method**
\`\`\`
GET
\`\`\`

**Request Parameters**

| Parameter | Type | Required | Description |
| --------- | ---- | -------- | ----------- |
| accessToken | String | Yes | Access token |

**Response**

\`\`\`json
{
  "data": [
    {
      "gatewayId": 11906,
      "name": "Gateway WL011906",
      "mac": "00:00:00:00:00:00",
      "version": 2,
      "onlineFlag": 1
    }
  ],
  "errorCode": 0,
  "errorMsg": "success"
}
\`\`\`
    `
  },
  {
    id: 'device-gateway-mapping',
    title: 'Device-Gateway Mapping',
    content: `
## Device-Gateway Mapping

The adapter maintains a mapping between locks and gateways. This is essential because WeLock's API requires specifying which gateway should communicate with a particular lock.

The mapping is stored in the \`GATEWAY_DEVICE_MAP\` dictionary in the code:

\`\`\`python
GATEWAY_DEVICE_MAP = {
    "23525216": {"gateway_number": "WL011906", "device_name": "WeLockISL68"},
    "23343221": {"gateway_number": "WL011755", "device_name": "WeLockD9RZ3"},
    # Additional mappings...
}
\`\`\`

If a lock is not found in this mapping, the adapter falls back to default gateway settings.
    `
  },
  {
    id: 'error-codes',
    title: 'Error Codes',
    content: `
## Error Codes

| Error Code | Description |
| ---------- | ----------- |
| 0 | Success |
| 100 | Server internal error |
| 10000 | Authentication failure |
| 10001 | Missing access token |
| 10002 | Access token refresh failed |
| 10003 | Token expired |
| 10010 | Invalid parameter |
| 10201 | Device does not exist |
| 10202 | Operation failed |
| 10203 | Gateway does not exist |
    `
  }
]; 